import React, { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery, useMutation } from 'convex/react'
import {
  UserCircleIcon,
  BookOpenIcon,
  UserGroupIcon,
  LockClosedIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline'
import { api } from '../convex_generated/api'
import { useAuth } from '../hooks/useAuth'

type Tab = 'works' | 'followers' | 'following'

const UserProfilePage: React.FC = () => {
  const { username } = useParams<{ username: string }>()
  const { user: currentUser, isAuthenticated } = useAuth()
  const [activeTab, setActiveTab] = useState<Tab>('works')

  const profileUser = useQuery(
    api.auth.getUserByUsername,
    username ? { username } : 'skip',
  )

  const isOwnProfile = currentUser && profileUser && currentUser._id === profileUser._id

  const followStatus = useQuery(
    api.users.checkFollowStatus,
    isAuthenticated && currentUser && profileUser && !isOwnProfile
      ? { followerId: currentUser._id, followingId: profileUser._id }
      : 'skip',
  )

  const followers = useQuery(
    api.users.getUserFollowers,
    profileUser ? { userId: profileUser._id, limit: 50 } : 'skip',
  )

  const following = useQuery(
    api.users.getUserFollowing,
    profileUser ? { userId: profileUser._id, limit: 50 } : 'skip',
  )

  const userStats = useQuery(
    api.auth.getUserStats,
    profileUser ? { userId: profileUser._id } : 'skip',
  )

  // Public branches for this user
  const userBranches = useQuery(
    api.branches.getPublicBranches,
    profileUser ? { authorId: profileUser._id } : 'skip',
  )

  const followUser = useMutation(api.users.followUser)
  const unfollowUser = useMutation(api.users.unfollowUser)

  // User search
  const [userSearchQuery, setUserSearchQuery] = useState('')
  const userSearchResults = useQuery(
    api.users.searchUsers,
    userSearchQuery.length >= 2 ? { query: userSearchQuery } : 'skip',
  )

  const handleFollowToggle = async () => {
    if (!currentUser || !profileUser) return
    if (followStatus?.isFollowing) {
      await unfollowUser({ followingId: profileUser._id })
    } else {
      await followUser({ followingId: profileUser._id })
    }
  }

  // Loading
  if (profileUser === undefined) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="animate-pulse space-y-6">
          <div className="flex items-center gap-6">
            <div className="w-20 h-20 rounded-full bg-gray-200 dark:bg-gray-700" />
            <div className="space-y-3">
              <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-40" />
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24" />
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Not found
  if (profileUser === null) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12 text-center">
        <UserCircleIcon className="w-16 h-16 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">User not found</h1>
        <p className="text-gray-500 dark:text-gray-400">
          No user with the username &ldquo;{username}&rdquo; was found.
        </p>
      </div>
    )
  }

  // Private profile
  const isPrivate = profileUser.profileVisibility === 'private' && !isOwnProfile
  if (isPrivate) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12 text-center">
        <LockClosedIcon className="w-16 h-16 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Private Profile</h1>
        <p className="text-gray-500 dark:text-gray-400">
          This user&apos;s profile is private.
        </p>
      </div>
    )
  }

  const roleBadge: Record<string, { label: string; color: string }> = {
    reader: { label: 'Reader', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
    writer: { label: 'Writer', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
    artist: { label: 'Artist', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' },
    admin: { label: 'Admin', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  }

  const badge = roleBadge[profileUser.role] ?? roleBadge.reader

  const tabs: { key: Tab; label: string; count?: number }[] = [
    { key: 'works', label: 'Works', count: userBranches?.length ?? 0 },
    { key: 'followers', label: 'Followers', count: userStats?.followersCount ?? 0 },
    { key: 'following', label: 'Following', count: userStats?.followingCount ?? 0 },
  ]

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Profile Header */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
          {/* Avatar */}
          {profileUser.avatar ? (
            <img
              src={profileUser.avatar}
              alt={profileUser.displayName}
              className="w-20 h-20 rounded-full object-cover ring-2 ring-gray-200 dark:ring-gray-700"
            />
          ) : (
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center text-white text-2xl font-bold">
              {profileUser.displayName
                .split(' ')
                .map((w: string) => w[0])
                .join('')
                .toUpperCase()
                .slice(0, 2)}
            </div>
          )}

          {/* Info */}
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-1">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {profileUser.displayName}
              </h1>
              <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${badge.color}`}>
                {badge.label}
              </span>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
              @{profileUser.username} · Joined {new Date(profileUser.createdAt).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
            </p>

            {/* Stats row */}
            <div className="flex items-center gap-6 text-sm">
              <button
                onClick={() => setActiveTab('works')}
                className="text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400"
              >
                <span className="font-semibold">{userStats?.branchesCount ?? 0}</span>{' '}
                <span className="text-gray-500 dark:text-gray-400">works</span>
              </button>
              <button
                onClick={() => setActiveTab('followers')}
                className="text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400"
              >
                <span className="font-semibold">{userStats?.followersCount ?? 0}</span>{' '}
                <span className="text-gray-500 dark:text-gray-400">followers</span>
              </button>
              <button
                onClick={() => setActiveTab('following')}
                className="text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400"
              >
                <span className="font-semibold">{userStats?.followingCount ?? 0}</span>{' '}
                <span className="text-gray-500 dark:text-gray-400">following</span>
              </button>
            </div>
          </div>

          {/* Follow button */}
          {isAuthenticated && !isOwnProfile && (
            <button
              onClick={handleFollowToggle}
              className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors ${
                followStatus?.isFollowing
                  ? 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/20 dark:hover:text-red-400'
                  : 'bg-primary-600 hover:bg-primary-700 text-white'
              }`}
            >
              {followStatus?.isFollowing ? 'Unfollow' : 'Follow'}
            </button>
          )}

          {isOwnProfile && (
            <Link
              to="/profile?edit=true"
              className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              Edit Profile
            </Link>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 dark:border-gray-700 mb-6">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.key
                ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span className="ml-1.5 text-xs text-gray-400 dark:text-gray-500">
                ({tab.count})
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'works' && (
        <div className="space-y-3">
          {!userBranches ? (
            Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 animate-pulse">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3 mb-2" />
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
              </div>
            ))
          ) : userBranches.length === 0 ? (
            <div className="text-center py-12">
              <BookOpenIcon className="w-10 h-10 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400">No public works yet</p>
            </div>
          ) : (
            userBranches.map((branch) => (
              <Link
                key={branch._id}
                to={`/reading/${branch._id}`}
                className="block bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 hover:border-primary-300 dark:hover:border-primary-700 hover:shadow-sm transition-all"
              >
                <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-sm mb-1">
                  {branch.title}
                </h3>
                <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                  <span>{branch.wordCount?.toLocaleString() ?? 0} words</span>
                  <span>·</span>
                  <span className="capitalize">{branch.category}</span>
                  {branch.rating > 0 && (
                    <>
                      <span>·</span>
                      <span>★ {branch.rating.toFixed(1)}</span>
                    </>
                  )}
                  <span>·</span>
                  <span>{new Date(branch.createdAt).toLocaleDateString()}</span>
                </div>
              </Link>
            ))
          )}
        </div>
      )}

      {/* Find people search — shown on followers/following tabs */}
      {(activeTab === 'followers' || activeTab === 'following') && isAuthenticated && (
        <div className="mb-6">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={userSearchQuery}
              onChange={(e) => setUserSearchQuery(e.target.value)}
              placeholder="Find people by name or username…"
              className="w-full pl-9 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
            />
          </div>
          {userSearchQuery.length >= 2 && userSearchResults && userSearchResults.length > 0 && (
            <div className="mt-2 space-y-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden shadow-lg">
              {userSearchResults.map((u) => (
                <Link
                  key={u._id}
                  to={`/user/${u.username}`}
                  onClick={() => setUserSearchQuery('')}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  {u.avatar ? (
                    <img src={u.avatar} alt={u.displayName} className="w-9 h-9 rounded-full object-cover" />
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center text-white text-sm font-bold">
                      {u.displayName?.[0]?.toUpperCase() ?? '?'}
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{u.displayName}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">@{u.username}</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
          {userSearchQuery.length >= 2 && userSearchResults && userSearchResults.length === 0 && (
            <p className="mt-2 text-xs text-gray-400 text-center py-2">No users found</p>
          )}
        </div>
      )}

      {activeTab === 'followers' && (
        <div className="space-y-2">
          {!followers ? (
            Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 animate-pulse">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700" />
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-32" />
                </div>
              </div>
            ))
          ) : (followers.followers?.length ?? 0) === 0 ? (
            <div className="text-center py-12">
              <UserGroupIcon className="w-10 h-10 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400">No followers yet</p>
            </div>
          ) : (
            followers.followers.map((f) => (
              <Link
                key={f.id}
                to={`/user/${f.username}`}
                className="flex items-center gap-3 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-3 hover:border-primary-300 dark:hover:border-primary-700 transition-colors"
              >
                {f.avatar ? (
                  <img src={f.avatar} alt={f.displayName} className="w-10 h-10 rounded-full object-cover" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center text-white text-sm font-bold">
                    {f.displayName?.[0]?.toUpperCase() ?? '?'}
                  </div>
                )}
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{f.displayName}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">@{f.username} · {f.role}</p>
                </div>
              </Link>
            ))
          )}
        </div>
      )}

      {activeTab === 'following' && (
        <div className="space-y-2">
          {!following ? (
            Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 animate-pulse">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700" />
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-32" />
                </div>
              </div>
            ))
          ) : (following.following?.length ?? 0) === 0 ? (
            <div className="text-center py-12">
              <UserGroupIcon className="w-10 h-10 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400">Not following anyone yet</p>
            </div>
          ) : (
            following.following.map((f) => (
              <Link
                key={f.id}
                to={`/user/${f.username}`}
                className="flex items-center gap-3 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-3 hover:border-primary-300 dark:hover:border-primary-700 transition-colors"
              >
                {f.avatar ? (
                  <img src={f.avatar} alt={f.displayName} className="w-10 h-10 rounded-full object-cover" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center text-white text-sm font-bold">
                    {f.displayName?.[0]?.toUpperCase() ?? '?'}
                  </div>
                )}
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{f.displayName}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">@{f.username} · {f.role}</p>
                </div>
              </Link>
            ))
          )}
        </div>
      )}
    </div>
  )
}

export default UserProfilePage
